import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Gauge,
  PackageX,
} from 'lucide-react';
import { useState } from 'react';
import type { ProductionSuggestion } from '../../../types/restaurant/RestaurantProductionPlannerTypes';
import {
  compareDecimal,
  formatDecimal,
  formatMoney,
} from '../../../utils/decimalValue';

interface Props {
  suggestion: ProductionSuggestion;
  quantity: string;
  onQuantityChange: (quantity: string) => void;
}

const ProductionSuggestionCard = ({
  suggestion,
  quantity,
  onQuantityChange,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const overMaximum =
    quantity !== '' &&
    compareDecimal(quantity, suggestion.maxProducibleQuantity) > 0;
  const invalidQuantity = quantity !== '' && compareDecimal(quantity, 0) <= 0;
  return (
    <article
      data-testid={`production-suggestion-${suggestion.recipeId}`}
      className={`overflow-hidden rounded-xl border bg-white transition dark:bg-boxdark ${
        suggestion.producible
          ? 'border-stroke hover:border-primary/40 dark:border-strokedark'
          : 'border-danger/30'
      }`}
    >
      <div className="p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  suggestion.producible
                    ? 'bg-success/10 text-success'
                    : 'bg-danger/10 text-danger'
                }`}
              >
                {suggestion.producible ? 'Disponible' : 'No disponible'}
              </span>
              <span className="text-xs text-bodydark2">
                Receta: {suggestion.recipeName}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-black dark:text-white">
              {suggestion.productName}
            </h3>
          </div>
          <div className="rounded-lg bg-primary/5 px-4 py-3 text-right">
            <p className="text-xs text-bodydark2">Máximo fabricable</p>
            <p className="text-xl font-bold text-primary">
              {formatDecimal(suggestion.maxProducibleQuantity)}
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
          <div className="rounded-lg bg-gray-2 p-3 dark:bg-meta-4">
            <p className="text-xs text-bodydark2">Costo total al máximo</p>
            <p className="font-semibold text-black dark:text-white">
              {formatMoney(suggestion.estimatedTotalCost)}
            </p>
          </div>
          <div className="rounded-lg bg-gray-2 p-3 dark:bg-meta-4">
            <p className="text-xs text-bodydark2">Costo unitario</p>
            <p className="font-semibold text-black dark:text-white">
              {formatMoney(suggestion.estimatedUnitCost)}
            </p>
          </div>
          <div className="col-span-2 rounded-lg bg-gray-2 p-3 dark:bg-meta-4 lg:col-span-1">
            <p className="flex items-center gap-1 text-xs text-bodydark2">
              <Gauge className="h-3.5 w-3.5" />
              Ingrediente limitante
            </p>
            <p className="truncate font-semibold text-black dark:text-white">
              {suggestion.limitingIngredientName || 'Sin limitante'}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-3 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row sm:items-end sm:justify-between">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary"
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Ver ingredientes ({suggestion.ingredients.length})
          </button>
          <div className="w-full sm:w-56">
            <label
              htmlFor={`production-quantity-${suggestion.recipeId}`}
              className="mb-1 block text-sm font-medium text-black dark:text-white"
            >
              Cantidad a fabricar
            </label>
            <input
              id={`production-quantity-${suggestion.recipeId}`}
              aria-label={`Cantidad a producir de ${suggestion.productName}`}
              type="number"
              min="0"
              step="0.000001"
              max={String(suggestion.maxProducibleQuantity)}
              disabled={!suggestion.producible}
              value={quantity}
              onChange={(event) => onQuantityChange(event.target.value)}
              placeholder="0"
              className={`w-full rounded border bg-transparent px-4 py-2.5 outline-none disabled:cursor-not-allowed disabled:bg-gray-2 dark:disabled:bg-meta-4 ${
                overMaximum || invalidQuantity
                  ? 'border-danger text-danger'
                  : 'border-stroke focus:border-primary dark:border-strokedark'
              }`}
            />
            {overMaximum && (
              <p className="mt-1 text-xs text-danger">
                Supera el máximo fabricable.
              </p>
            )}
            {invalidQuantity && (
              <p className="mt-1 text-xs text-danger">
                Debe ser mayor que cero.
              </p>
            )}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-stroke bg-gray-2/30 p-5 dark:border-strokedark dark:bg-meta-4/10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="text-xs uppercase text-bodydark2">
                <tr>
                  <th className="pb-3">Materia prima</th>
                  <th className="pb-3">Por unidad</th>
                  <th className="pb-3">Disponible</th>
                  <th className="pb-3">Máximo</th>
                  <th className="pb-3">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stroke dark:divide-strokedark">
                {suggestion.ingredients.map((ingredient) => (
                  <tr key={ingredient.rawMaterialId}>
                    <td className="py-3 font-medium text-black dark:text-white">
                      {ingredient.rawMaterialName}
                      {ingredient.limiting && (
                        <span className="ml-2 rounded bg-warning/10 px-2 py-0.5 text-xs text-warning">
                          Limitante
                        </span>
                      )}
                    </td>
                    <td className="py-3">
                      {formatDecimal(ingredient.requiredPerUnit)}{' '}
                      {ingredient.unitCode}
                    </td>
                    <td
                      className={`py-3 ${
                        compareDecimal(ingredient.availableQuantity, 0) < 0
                          ? 'font-semibold text-danger'
                          : ''
                      }`}
                    >
                      {formatDecimal(ingredient.availableQuantity)}{' '}
                      {ingredient.unitCode}
                    </td>
                    <td className="py-3">
                      {formatDecimal(ingredient.maxProducibleQuantity)}
                    </td>
                    <td className="py-3">
                      {ingredient.available ? (
                        <span className="inline-flex items-center gap-1 text-success">
                          <CheckCircle2 className="h-4 w-4" />
                          Suficiente
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-danger">
                          <PackageX className="h-4 w-4" />
                          Faltante
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {suggestion.missingIngredients.length > 0 && (
            <div className="mt-4 rounded-lg border border-danger/20 bg-danger/5 p-3 text-sm text-danger">
              <p className="mb-1 flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" />
                Materias primas faltantes
              </p>
              {suggestion.missingIngredients.map((item) => (
                <p key={item.rawMaterialId}>
                  {item.rawMaterialName}: faltan{' '}
                  {formatDecimal(item.missingQuantity)} {item.unitCode}.
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default ProductionSuggestionCard;
